import { CreativeFilters } from '../utils/creativesFiltersUtils';
import { CreativeTypeValue } from '../utils/consts';
import { IN_OPERATOR } from '../utils/reportsConsts';
import { getCohortFieldAndPeriod } from './metricsUtils';
import { ASYNC_FILTER_TO_FIELD_CONFIG } from './dimensionsUtils';
import { MULTI_VALUED_FIELDS_BY_GROUP } from './creativesFormattingUtils';
import { CREATIVE_IMAGE_HASH, EMPTY_CREATIVE_IMAGE_HASH } from './consts';

const buildBasicFilter = (dimension, values, operator = IN_OPERATOR) => ({
    dimension,
    operator,
    values,
    options: { no_lowercase: true },
});

const getDefaultFilters = (app, source) => [
    buildBasicFilter('creative_type', [
        CreativeTypeValue.image,
        CreativeTypeValue.video,
        CreativeTypeValue.playable,
        CreativeTypeValue.carousel,
    ]),
    buildBasicFilter(CreativeFilters.APP.toLowerCase(), [app]),
    buildBasicFilter(CreativeFilters.SOURCE.toLowerCase(), [source]),
];

const buildAdditionalFilter = ({ filterName, values }) => {
    const originalField = ASYNC_FILTER_TO_FIELD_CONFIG[filterName]?.originalField;
    if (!originalField) return null;
    return buildBasicFilter(originalField, values);
};

export const getCreativesDefaultParams = (dateRange, app, source, metrics, cohortFields, additionalFilters) => {
    const [cohortMetrics, cohortPeriods, conversionEvents] = cohortFields.reduce(
        (acc, field) => {
            const [metric, period] = getCohortFieldAndPeriod(field.value);
            if (!period) {
                acc[2].push(metric);
            } else {
                acc[0].push(metric);
                acc[1].push(period);
            }
            return acc;
        },
        [[], [], []]
    );

    const additionalFormattedFilters = (additionalFilters || []).map(buildAdditionalFilter).filter(Boolean);
    const filters = getDefaultFilters(app, source).concat(additionalFormattedFilters);

    return {
        api_version: 1,
        dimensions: [CREATIVE_IMAGE_HASH],
        cohort_metrics: [...new Set(cohortMetrics)],
        cohort_periods: [...new Set(cohortPeriods)],
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
        metrics: metrics.map(({ value }) => value).concat(conversionEvents),
        show_creative_type: true,
        filters,
        post_process_filters: [
            {
                field: CREATIVE_IMAGE_HASH,
                value: EMPTY_CREATIVE_IMAGE_HASH,
                valueTo: null,
                operator: 'not equals',
            },
        ],
        is_creative_reporting_query: true,
        is_creative_gallery: true,
        displayNonGraphical: false,
        time_breakdown: ['all'],
        fuzzy: false,
        is_default: false,
        flags: [],
        display_alignment: false,
    };
};

export const getMainCreativesQueryParams = (dateRange, app, source, metrics, cohortFields, additionalFilters) => {
    const requestParams = getCreativesDefaultParams(dateRange, app, source, metrics, cohortFields, additionalFilters);
    const { dimensions } = requestParams;

    return {
        ...requestParams,
        dimensions: dimensions.concat(['creative_embedding_url']),
        technical_specs_dimensions: [
            'creative_width',
            'creative_height',
            'creative_orientation',
            'creative_video_duration_range',
        ],
        creative_measurement_dimensions: ['creative_type'],
    };
};

export const getMultiValuedDimensionsQueryParams = (
    dateRange,
    app,
    source,
    metrics,
    cohortFields,
    additionalFilters
) => {
    const requestParams = getCreativesDefaultParams(dateRange, app, source, metrics, cohortFields, additionalFilters);
    const { dimensions, ...otherMultiValuedDimensions } = MULTI_VALUED_FIELDS_BY_GROUP;

    return {
        ...requestParams,
        dimensions: requestParams.dimensions.concat(dimensions),
        ...otherMultiValuedDimensions,
    };
};
